import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Mail, CheckCircle, XCircle, Trash2 } from 'lucide-react';

interface EmailSubscriber {
  id: number;
  email: string;
  subscriptionTier: string;
  subscriptionStatus: string;
  alertThreshold: number;
  categories: string[];
  digestType: string;
  language: string;
  isActive: boolean;
  isVerified: boolean;
  lastEmailSent?: string;
  emailCount: number;
  createdAt: string;
}

interface SubscribersTableProps {
  subscribers: EmailSubscriber[];
  onDelete: (id: number) => void;
  onSendTest?: (email: string) => void;
  loading?: boolean;
}

export default function SubscribersTable({ 
  subscribers, 
  onDelete,
  onSendTest,
  loading = false 
}: SubscribersTableProps) {
  
  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
    );
  }

  if (subscribers.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No subscribers found
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200';
      case 'PAUSED': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200';
      case 'CANCELLED': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200';
      case 'EXPIRED': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200';
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'TRIAL': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200';
      case 'PREMIUM': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-200';
      case 'PREMIUM_PLUS': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200';
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Subscriber
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Subscription
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Preferences
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Activity
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {subscribers.map((subscriber) => (
            <tr key={subscriber.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {subscriber.email}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-1">
                      {subscriber.isVerified ? (
                        <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                          <CheckCircle size={12} />
                          Verified
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                          <XCircle size={12} />
                          Unverified
                        </span>
                      )}
                      {subscriber.isActive ? (
                        <span className="text-green-600 dark:text-green-400">Active</span>
                      ) : (
                        <span className="text-red-600 dark:text-red-400">Inactive</span>
                      )}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="space-y-1">
                  <span className={`inline-flex text-xs px-2 py-1 rounded-full ${getTierColor(subscriber.subscriptionTier)}`}>
                    {subscriber.subscriptionTier}
                  </span>
                  <br />
                  <span className={`inline-flex text-xs px-2 py-1 rounded-full ${getStatusColor(subscriber.subscriptionStatus)}`}>
                    {subscriber.subscriptionStatus}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900 dark:text-white">
                  <div>Type: <span className="font-medium">{subscriber.digestType || 'none'}</span></div>
                  <div>Threshold: <span className="font-medium">{subscriber.alertThreshold}</span></div>
                  <div>Categories: <span className="text-xs">{(subscriber.categories || []).join(', ')}</span></div>
                  <div>Language: <span className="font-medium">{subscriber.language.toUpperCase()}</span></div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900 dark:text-white">
                  <div>Emails sent: <span className="font-medium">{subscriber.emailCount}</span></div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {subscriber.lastEmailSent 
                      ? `Last: ${formatDistanceToNow(new Date(subscriber.lastEmailSent))} ago`
                      : 'Never sent'
                    }
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Joined {formatDistanceToNow(new Date(subscriber.createdAt))} ago
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <button
                  onClick={() => {
                    if (window.confirm(`Delete subscriber ${subscriber.email}?`)) {
                      onDelete(subscriber.id);
                    }
                  }}
                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                >
                  <Trash2 size={16} />
                </button>
                {onSendTest && (
                  <button
                    onClick={() => onSendTest(subscriber.email)}
                    className="ml-3 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    title="Send test alert email"
                  >
                    <Mail size={16} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
